/**
 * Capability detection and initial tier choice.
 *
 * Browser APIs used, with absence handled:
 * - navigator.gpu (WebGPU). Missing in some browsers and in insecure contexts.
 * - WebGL2 via canvas.getContext('webgl2'). Fallback probe.
 * - navigator.deviceMemory. Chromium-only; undefined elsewhere (MDN).
 * - navigator.hardwareConcurrency. Widely available, may be clamped.
 * - navigator.userAgentData.mobile. Chromium-only; falls back to a media query.
 *   Reported in the profile for logging; no longer used to pick a tier.
 */
import type { TierId } from '../config/tiers';

export interface DeviceProfile {
  backend: 'webgpu' | 'webgl2' | 'none';
  hardwareConcurrency: number | null;
  /** navigator.deviceMemory, may be undefined. */
  deviceMemoryGb: number | null;
  maxTextureSize: number | null;
  isMobileHint: boolean;
  benchmarkMsPerFrame: number | null;
}

interface NavigatorExtras {
  deviceMemory?: number;
  userAgentData?: { mobile?: boolean };
}

async function probeWebGpu(): Promise<number | null> {
  if (!('gpu' in navigator)) return null;
  try {
    const adapter = await navigator.gpu.requestAdapter();
    return adapter ? adapter.limits.maxTextureDimension2D : null;
  } catch {
    return null;
  }
}

function probeWebGl2(): number | null {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    if (!gl) return null;
    const size = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return size;
  } catch {
    return null;
  }
}

export async function detectDevice(): Promise<DeviceProfile> {
  const nav = navigator as Navigator & NavigatorExtras;
  const gpuTex = await probeWebGpu();
  const glTex = gpuTex === null ? probeWebGl2() : null;

  let isMobileHint = false;
  if (typeof nav.userAgentData?.mobile === 'boolean') {
    isMobileHint = nav.userAgentData.mobile;
  } else if (typeof matchMedia === 'function') {
    isMobileHint = matchMedia('(pointer: coarse)').matches;
  }

  return {
    backend: gpuTex !== null ? 'webgpu' : glTex !== null ? 'webgl2' : 'none',
    hardwareConcurrency: nav.hardwareConcurrency > 0 ? nav.hardwareConcurrency : null,
    deviceMemoryGb: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    maxTextureSize: gpuTex ?? glTex,
    isMobileHint,
    benchmarkMsPerFrame: null,
  };
}

/** Benchmark (median work-time signal at medium) above this fraction of budget: start at low. */
export const LOW_START_RATIO = 1.15;
/**
 * Benchmark below this fraction of the frame budget: start at high.
 *
 * Calibrated 2026-10-21 from bench/calibration/ (three devices). GPU time
 * at high was 2.5x to 2.8x GPU time at medium on every device, with CPU
 * time flat. If medium uses fraction f of the budget, high is expected at
 * about 2.8 f; requiring 2.8 f x 1.1 margin <= 1 gives f <= 0.32, rounded
 * to 0.35. Measured medium work times were 0.8, 1.8, and 1.0 ms (5% to
 * 11% of budget), and high held 60 fps on all three.
 */
export const HIGH_START_RATIO = 0.35;

/**
 * Starting tier.
 *
 * Before the startup benchmark (benchmarkMsPerFrame === null) this is a
 * conservative guess from device hints. After it, the benchmark decides.
 * The benchmark is the governor signal (work time when frames are on time,
 * frame interval when they are not) measured at medium.
 *
 * Never starts at ultra. On one measured device (Inspiron 7405) ultra
 * dropped to ~30 fps while work time said it was cheap, so ultra is only
 * reached by a governor probe that can fail and back off.
 *
 * Removed after calibration: "mobile => low". A measured iPhone 14 Pro Max
 * held 60 fps at ultra. A genuinely weak phone is still caught, because a
 * slow benchmark at medium sends it to low.
 */
export function chooseInitialTier(p: DeviceProfile): TierId {
  if (p.backend === 'none') return 'low';
  const budgetMs = 1000 / 60;

  if (p.benchmarkMsPerFrame === null) {
    // TODO(calibrate): no weak device measured yet; these cutoffs are guesses.
    const weak =
      (p.deviceMemoryGb !== null && p.deviceMemoryGb <= 2) ||
      (p.hardwareConcurrency !== null && p.hardwareConcurrency <= 2);
    return weak ? 'low' : 'medium';
  }

  const b = p.benchmarkMsPerFrame;
  if (b > budgetMs * LOW_START_RATIO) return 'low';
  if (b < budgetMs * HIGH_START_RATIO) return 'high';
  return 'medium';
}

/** Fixed-capacity frame-time sampler for the startup micro-benchmark. */
export class FrameSampler {
  private readonly samples: Float64Array;
  private n = 0;

  constructor(capacity: number) {
    this.samples = new Float64Array(capacity);
  }

  push(ms: number): void {
    if (this.n < this.samples.length) this.samples[this.n++] = ms;
  }

  get count(): number {
    return this.n;
  }

  get full(): boolean {
    return this.n >= this.samples.length;
  }

  /** Median of collected samples, or null if none. Sorts in place. */
  median(): number | null {
    if (this.n === 0) return null;
    const view = this.samples.subarray(0, this.n).sort();
    const mid = this.n >> 1;
    return this.n % 2 === 1
      ? (view[mid] as number)
      : ((view[mid - 1] as number) + (view[mid] as number)) / 2;
  }
}

/** ~2 s at 60 Hz. */
export const BENCHMARK_DURATION_MS = 2000;
